import { useState } from 'react';
import { Check } from 'lucide-react';
import { Button, Drawer } from '@/design/components';
import { useImageUrl } from '@/features/products/api';
import { uploadImage } from '@/features/products/admin/upload';
import { cn } from '@/lib/cn';
import { errorMessage } from '@/lib/errors';
import type { CampaignValues } from './campaignValues';

const FIELD = 'min-h-11 w-full rounded-sm border border-muted bg-surface px-3 text-body text-ink';

/**
 * סידור ויטרינה: שם, הוראות, תמונת יעד, מועד וסניפים.
 *
 * אותו חלון ליצירה ולעריכה. כשהיתה רק יצירה, תיקון של שעה
 * בהוראות דרש למחוק את הסידור — ואיתו את כל הצילומים שסניפים
 * כבר הגישו.
 */
export function CampaignDialog({
  initial,
  branches,
  isSaving,
  onSave,
  onClose,
}: {
  initial: CampaignValues;
  branches: { id: string; name: string; color?: string | null }[];
  isSaving: boolean;
  onSave: (values: CampaignValues) => Promise<unknown>;
  onClose: () => void;
}) {
  const [values, setValues] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reference = useImageUrl(values.referenceImagePath);

  const set = <K extends keyof CampaignValues>(key: K, value: CampaignValues[K]) =>
    setValues((current) => ({ ...current, [key]: value }));

  /** מחושב מהמצב הקודם: שתי לחיצות באותו tick היו דורסות זו את זו. */
  const toggleBranch = (id: string) =>
    setValues((current) => ({
      ...current,
      branchIds: current.branchIds.includes(id)
        ? current.branchIds.filter((value) => value !== id)
        : [...current.branchIds, id],
    }));

  const save = () => {
    setError(null);
    if (!values.title.trim()) {
      setError('צריך לתת לסידור שם.');
      return;
    }
    void onSave(values).catch((caught: unknown) => setError(errorMessage(caught)));
  };

  return (
    <Drawer
      title={initial.id ? 'עריכת סידור' : 'סידור ויטרינה חדש'}
      subtitle="תמונת היעד וההוראות הן מה שהסניף רואה"
      onClose={onClose}
      footer={(close) => (
        <div className="flex flex-wrap gap-2">
          <Button variant="primary" disabled={isSaving || busy} onClick={save}>
            {initial.id ? 'שמירת השינויים' : 'שליחה לסניפים'}
          </Button>
          <Button variant="secondary" onClick={close}>
            ביטול
          </Button>
        </div>
      )}
    >
      <div className="grid gap-4">
        <div className="grid gap-1">
          <label htmlFor="title" className="text-label font-medium">
            שם הסידור
          </label>
          <input
            id="title"
            autoFocus
            value={values.title}
            onChange={(event) => set('title', event.target.value)}
            placeholder="למשל: ויטרינת חורף"
            className={FIELD}
          />
        </div>

        <div className="grid gap-1">
          <label htmlFor="instructions" className="text-label font-medium">
            הוראות
          </label>
          <textarea
            id="instructions"
            rows={4}
            value={values.instructions}
            onChange={(event) => set('instructions', event.target.value)}
            placeholder="מה צריך לעמוד איפה, ומה חשוב שייראה"
            className={`${FIELD} py-2`}
          />
        </div>

        <div className="grid gap-1">
          <label htmlFor="due" className="text-label font-medium">
            עד מתי
          </label>
          <input
            id="due"
            type="date"
            value={values.dueDate}
            onChange={(event) => set('dueDate', event.target.value)}
            className={FIELD}
          />
        </div>

        <div className="grid gap-2">
          <label htmlFor="reference" className="text-label font-medium">
            תמונת היעד
          </label>

          {/* מוצגת ולא רק "תמונה נטענה": בלי לראות אותה אי אפשר
              לדעת אם הועלה הקובץ הנכון */}
          {reference.data && (
            <img
              src={reference.data}
              alt=""
              className="max-h-48 w-full rounded-sm border border-line object-contain"
            />
          )}

          <input
            id="reference"
            type="file"
            accept="image/*"
            disabled={busy}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (!file) return;
              setBusy(true);
              setError(null);
              uploadImage('ops', file)
                .then((path) => set('referenceImagePath', path))
                .catch((caught: unknown) => setError(errorMessage(caught)))
                .finally(() => setBusy(false));
            }}
            className="text-body-sm"
          />
          {busy && <p className="m-0 text-body-sm text-muted">מעלה…</p>}
          {!busy && values.referenceImagePath && (
            <button
              type="button"
              onClick={() => set('referenceImagePath', null)}
              className="min-h-9 cursor-pointer justify-self-start rounded-sm bg-transparent px-2 text-label text-muted underline hover:text-ink"
            >
              הסרת התמונה
            </button>
          )}
        </div>

        <fieldset className="m-0 grid gap-2 border-0 p-0">
          <legend className="p-0 text-label font-medium">לאילו סניפים</legend>
          <div className="flex flex-wrap gap-2">
            <Chip active={values.branchIds.length === 0} onClick={() => set('branchIds', [])}>
              כל הסניפים
            </Chip>
            {branches.map((branch) => (
              <Chip
                key={branch.id}
                active={values.branchIds.includes(branch.id)}
                color={branch.color}
                onClick={() => toggleBranch(branch.id)}
              >
                {branch.name}
              </Chip>
            ))}
          </div>
          <p className="m-0 text-body-sm text-muted">
            בלי בחירה הסידור נשלח לכל הסניפים, גם לכאלה שייפתחו בעתיד.
          </p>
        </fieldset>

        {error && (
          <p role="alert" className="m-0 text-body-sm text-bad">
            {error}
          </p>
        )}
      </div>
    </Drawer>
  );
}

function Chip({
  active,
  color,
  onClick,
  children,
}: {
  active: boolean;
  color?: string | null;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'inline-flex min-h-11 cursor-pointer items-center gap-1.5 rounded-pill border px-3 text-body-sm transition-colors',
        active
          ? 'border-ink font-semibold text-ink'
          : 'border-muted bg-transparent font-medium text-ink hover:bg-cream',
        active && !color && 'bg-olive',
      )}
      style={active && color ? { backgroundColor: color } : undefined}
    >
      {active && <Check size={16} strokeWidth={2.5} aria-hidden="true" className="shrink-0" />}
      {children}
    </button>
  );
}
